
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { ParametrosFinanceiros, KitSolar } from "@/types";
import { ArrowLeft, DollarSign, Calculator, Percent, Wrench, Building, Receipt } from "lucide-react";

interface ParametrosFinanceirosFormProps {
  onDadosSalvos: (dados: ParametrosFinanceiros) => void;
  dadosIniciais?: ParametrosFinanceiros;
  onVoltar?: () => void;
  kitSolar?: KitSolar | null;
}

export const ParametrosFinanceirosForm = ({
  onDadosSalvos,
  dadosIniciais,
  onVoltar,
  kitSolar,
}: ParametrosFinanceirosFormProps) => {
  const valorKit = kitSolar?.precoTotal || 0;
  
  const [custoMaoDeObra, setCustoMaoDeObra] = useState<number>(
    dadosIniciais?.custoMaoDeObra || Math.round(valorKit * 0.1) // Changed from 0.05 to 0.1 (10%)
  );
  const [custoEngenharia, setCustoEngenharia] = useState<number>(
    dadosIniciais?.custoEngenharia || Math.round(valorKit * 0.1)
  );
  const [margemLucro, setMargemLucro] = useState<number>(
    dadosIniciais?.margemLucro || 20
  );
  const [impostos, setImpostos] = useState<number>(
    dadosIniciais?.impostos || 4 // Changed from 7 to 4 (4%)
  );
  const [outrasDespesas, setOutrasDespesas] = useState<number>(
    dadosIniciais?.outrasDespesas || 0
  );
  
  // Recalcular valores quando o kit mudar
  useEffect(() => {
    if (valorKit > 0) {
      if (!dadosIniciais?.custoMaoDeObra) {
        setCustoMaoDeObra(Math.round(valorKit * 0.1)); // Changed from 0.05 to 0.1 (10%)
      }
      if (!dadosIniciais?.custoEngenharia) {
        setCustoEngenharia(Math.round(valorKit * 0.1));
      }
    }
  }, [valorKit, dadosIniciais]);
  
  // Calcular valor total
  const calculaValorTotal = () => {
    const valorMaoDeObra = custoMaoDeObra || 0;
    const valorEngenharia = custoEngenharia || 0;
    const valorMargemLucro = valorKit * (margemLucro / 100);
    const valorImpostos = valorKit * (impostos / 100);
    const valorOutrasDespesas = outrasDespesas || 0;
    
    return valorKit + valorMaoDeObra + valorEngenharia + valorMargemLucro + valorImpostos + valorOutrasDespesas;
  };
  
  const valorTotal = calculaValorTotal();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const parametrosFinanceiros: ParametrosFinanceiros = {
      custoMaoDeObra,
      custoEngenharia,
      margemLucro,
      impostos,
      outrasDespesas,
      valorTotal,
    };

    onDadosSalvos(parametrosFinanceiros);
  };

  return (
    <Card className="shadow-md animate-fade-in">
      <CardHeader className="bg-green-50/50 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-green-700 text-xl md:text-2xl">
            <Receipt className="h-6 w-6 text-green-600" />
            Parâmetros Financeiros
          </CardTitle>
          <CardDescription className="text-base">
            Defina os parâmetros financeiros para cálculo da proposta
          </CardDescription>
        </div>
        {onVoltar && (
          <Button
            variant="outline"
            size="sm"
            onClick={onVoltar}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-5 md:p-7">
        <form onSubmit={handleSubmit} className="space-y-6">
          {kitSolar && (
            <div className="rounded-lg border bg-green-50/70 p-4 shadow-sm mb-6">
              <Label className="text-sm font-medium text-gray-600 mb-2 block">Valor do Kit Solar</Label>
              <div className="flex items-center gap-2">
                <DollarSign className="h-6 w-6 text-green-600" />
                <span className="text-2xl md:text-3xl font-bold text-green-700">
                  {valorKit.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </span>
              </div>
            </div>
          )}

          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-3 p-4 rounded-lg border hover:shadow-md transition-shadow bg-white">
              <Label htmlFor="custoMaoDeObra" className="flex items-center gap-2 text-base font-medium">
                <Wrench className="h-5 w-5 text-green-600" />
                Mão de Obra (R$)
              </Label>
              <Input
                id="custoMaoDeObra"
                type="number"
                value={custoMaoDeObra}
                onChange={(e) => setCustoMaoDeObra(Number(e.target.value))}
                placeholder="0.00"
                min={0}
                step="0.01"
                className="text-right text-lg font-medium"
              />
              {valorKit > 0 && (
                <p className="text-sm text-gray-600 font-medium mt-1">
                  Sugestão: 10% do valor do kit (
                  <span className="font-bold text-green-700">
                    {(valorKit * 0.1).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </span>)
                </p>
              )}
            </div>

            <div className="space-y-3 p-4 rounded-lg border hover:shadow-md transition-shadow bg-muted/10">
              <Label htmlFor="custoEngenharia" className="flex items-center gap-2 text-base font-medium">
                <Building className="h-5 w-5 text-green-600" />
                Engenharia (R$)
              </Label>
              <Input
                id="custoEngenharia"
                type="number"
                value={custoEngenharia}
                onChange={(e) => setCustoEngenharia(Number(e.target.value))}
                placeholder="0.00"
                min={0}
                step="0.01"
                className="text-right bg-muted/20 text-lg font-medium"
                disabled={true}
              />
              {valorKit > 0 && (
                <p className="text-sm text-gray-600 font-medium mt-1">
                  Valor fixado em 10% do valor do kit (
                  <span className="font-bold text-green-700">
                    {(valorKit * 0.1).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </span>)
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 mt-4">
            <div className="space-y-3 p-4 rounded-lg border hover:shadow-md transition-shadow bg-white">
              <Label htmlFor="margemLucro" className="flex items-center gap-2 text-base font-medium">
                <Percent className="h-5 w-5 text-green-600" />
                Margem de Lucro (%)
              </Label>
              <Input
                id="margemLucro"
                type="number"
                value={margemLucro}
                onChange={(e) => setMargemLucro(Number(e.target.value))}
                placeholder="20"
                min={0}
                max={100}
                className="text-right text-lg font-medium"
              />
              {valorKit > 0 && (
                <p className="text-sm text-gray-600 font-medium mt-1">
                  Valor: <span className="font-bold text-green-700">
                    {(valorKit * (margemLucro / 100)).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </span>
                </p>
              )}
            </div>

            <div className="space-y-3 p-4 rounded-lg border hover:shadow-md transition-shadow bg-white">
              <Label htmlFor="impostos" className="flex items-center gap-2 text-base font-medium">
                <Receipt className="h-5 w-5 text-green-600" />
                Impostos (%)
              </Label>
              <Input
                id="impostos"
                type="number"
                value={impostos}
                onChange={(e) => setImpostos(Number(e.target.value))}
                placeholder="4"
                min={0}
                max={100}
                className="text-right text-lg font-medium"
              />
              {valorKit > 0 && (
                <p className="text-sm text-gray-600 font-medium mt-1">
                  Valor: <span className="font-bold text-green-700">
                    {(valorKit * (impostos / 100)).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </span>
                </p>
              )}
            </div>
          </div>

          <div className="space-y-3 p-4 rounded-lg border hover:shadow-md transition-shadow bg-white mt-4">
            <Label htmlFor="outrasDespesas" className="flex items-center gap-2 text-base font-medium">
              <DollarSign className="h-5 w-5 text-green-600" />
              Outras Despesas (R$)
            </Label>
            <Input
              id="outrasDespesas"
              type="number"
              value={outrasDespesas}
              onChange={(e) => setOutrasDespesas(Number(e.target.value))}
              placeholder="0.00"
              min={0}
              step="0.01"
              className="text-right text-lg font-medium"
            />
          </div>
          
          <div className="mt-6 p-6 bg-gradient-to-r from-green-50 to-green-100 rounded-lg shadow-md">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
              <Label className="text-xl font-semibold text-green-800">Valor de Venda do Sistema</Label>
              <span className="text-3xl font-bold text-green-700">
                {valorTotal.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </span>
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-green-600 hover:bg-green-700 mt-5 text-white flex items-center justify-center gap-2 py-6 text-lg font-medium"
          >
            <Calculator className="h-6 w-6" />
            Salvar Parâmetros e Calcular
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
